import {
  BroadcastCustomersQuery,
  CustomerOrderByWithRelationInput,
  CustomerWhereInput,
  QueryMode,
  SortOrder,
} from '@/__generated__/graphql';
import {
  broadcastCustomersDocument,
  sendBroadcastDocument,
} from '@/features/customer/graphql/send-broadcast';
import { broadcastQueueStatusDocument } from '@/features/broadcast/graphql/queue-status';
import {
  BroadcastProgress,
  BroadcastRun,
} from '@/features/broadcast/components/BroadcastProgress';
import { useApolloClient, useMutation, useQuery } from '@apollo/client';
import {
  Alert,
  Avatar,
  Badge,
  Button,
  Checkbox,
  Group,
  Loader,
  NumberInput,
  Paper,
  Stack,
  Text,
  TextInput,
  Textarea,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { useDebouncedValue, useLocalStorage } from '@mantine/hooks';
import { modals } from '@mantine/modals';
import { showNotification } from '@mantine/notifications';
import { DataTable, DataTableColumn, DataTableSortStatus } from 'mantine-datatable';
import dayjs from 'dayjs';
import { useEffect, useMemo, useState } from 'react';

interface BroadcastFormValues {
  message: string;
  url: string;
  sendLimit: number | '';
  sendSkip: number | '';
  name: string;
  activeOnly: boolean;
  withMessagesOnly: boolean;
}

const DEFAULT_BROADCAST_LIMIT = 100;
const SNAPSHOT_TABLE_HEIGHT = 320;
const SNAPSHOT_PAGE_SIZE_OPTIONS = [25, 50, 100, 250];
const DEFAULT_SNAPSHOT_PAGE_SIZE = SNAPSHOT_PAGE_SIZE_OPTIONS[0];
type BroadcastCustomer = BroadcastCustomersQuery['customers'][number];

interface SnapshotRecipientsTableProps {
  mode: 'local' | 'query';
  records?: BroadcastCustomer[];
  where?: CustomerWhereInput;
  orderBy?: CustomerOrderByWithRelationInput[];
  baseSkip?: number;
  totalRecords: number;
}

const buildWhere = ({
  name,
  activeOnly,
  withMessagesOnly,
}: Pick<BroadcastFormValues, 'name' | 'activeOnly' | 'withMessagesOnly'>): CustomerWhereInput => {
  const where: CustomerWhereInput = {};

  if (name.trim().length > 0) {
    where.name = { contains: name.trim(), mode: QueryMode.Insensitive };
  }

  if (activeOnly) {
    where.active = { equals: true };
  }

  if (withMessagesOnly) {
    where.messages = { some: { isEcho: { equals: false } } };
  }

  return where;
};

const buildOrderBy = (
  sortStatus: DataTableSortStatus<BroadcastCustomer>
): CustomerOrderByWithRelationInput[] => {
  const direction = sortStatus.direction as SortOrder;

  switch (sortStatus.columnAccessor) {
    case 'active':
      return [{ active: direction }];
    case 'createdAt':
      return [{ createdAt: direction }];
    case 'updatedAt':
      return [{ updatedAt: direction }];
    case 'name':
      return [{ name: direction }];
    default:
      return [{ updatedAt: SortOrder.Desc }];
  }
};

const SnapshotRecipientsTable = ({
  mode,
  records,
  where,
  orderBy,
  baseSkip,
  totalRecords,
}: SnapshotRecipientsTableProps) => {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_SNAPSHOT_PAGE_SIZE);

  const maxPage = useMemo(
    () => Math.max(1, Math.ceil(totalRecords / pageSize)),
    [totalRecords, pageSize]
  );
  const pageOffset = useMemo(() => (page - 1) * pageSize, [page, pageSize]);
  const pageTake = useMemo(
    () => Math.max(Math.min(pageSize, totalRecords - pageOffset), 0),
    [pageOffset, pageSize, totalRecords]
  );

  const snapshotVariables = useMemo(
    () => ({
      where: where ?? {},
      orderBy: orderBy ?? [{ updatedAt: SortOrder.Desc }],
      skip: (baseSkip ?? 0) + pageOffset,
      take: pageTake,
    }),
    [where, orderBy, baseSkip, pageOffset, pageTake]
  );

  const { data, loading } = useQuery(broadcastCustomersDocument, {
    variables: snapshotVariables,
    fetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true,
    skip: mode !== 'query' || pageTake === 0,
  });

  const paginatedRecords = useMemo(() => {
    if (mode === 'query') {
      return data?.customers ?? [];
    }

    const localRecords = records ?? [];
    return localRecords.slice(pageOffset, pageOffset + pageSize);
  }, [data?.customers, mode, pageOffset, pageSize, records]);

  const columns = useMemo<DataTableColumn<BroadcastCustomer>[]>(
    () => [
      {
        accessor: 'name',
        title: 'Meno',
        render: ({ name, avatar }) => (
          <Group gap="sm" wrap="nowrap">
            <Avatar src={avatar} size="sm" />
            <Text size="sm">{name}</Text>
          </Group>
        ),
      },
      {
        accessor: 'active',
        title: 'Aktívny',
        render: ({ active }) => (
          <Badge color={active ? 'teal' : 'red'}>{active ? 'Áno' : 'Nie'}</Badge>
        ),
      },
      {
        accessor: 'reason',
        title: 'Reason',
        render: ({ reason }) => (reason && reason.trim().length > 0 ? reason : '-'),
      },
    ],
    []
  );

  useEffect(() => {
    if (page > maxPage) {
      setPage(maxPage);
    }
  }, [maxPage, page]);

  return (
    <DataTable
      withTableBorder
      striped
      highlightOnHover
      fetching={mode === 'query' ? loading : false}
      idAccessor="id"
      height={SNAPSHOT_TABLE_HEIGHT}
      scrollAreaProps={{ type: 'auto' }}
      records={paginatedRecords}
      columns={columns}
      totalRecords={totalRecords}
      recordsPerPage={pageSize}
      page={page}
      onPageChange={setPage}
      recordsPerPageOptions={SNAPSHOT_PAGE_SIZE_OPTIONS}
      onRecordsPerPageChange={(value) => {
        setPageSize(value);
        setPage(1);
      }}
    />
  );
};

export const BroadcastPage = () => {
  const client = useApolloClient();
  const [run, setRun] = useState<BroadcastRun | null>(null);

  const [storedMessage, setStoredMessage] = useLocalStorage<string>({
    key: 'broadcast-message',
    defaultValue: '',
    getInitialValueInEffect: false,
  });
  const [storedUrl, setStoredUrl] = useLocalStorage<string>({
    key: 'broadcast-url',
    defaultValue: '',
    getInitialValueInEffect: false,
  });
  const [storedSendLimit, setStoredSendLimit] = useLocalStorage<number>({
    key: 'broadcast-send-limit',
    defaultValue: DEFAULT_BROADCAST_LIMIT,
    getInitialValueInEffect: false,
  });
  const [storedSendSkip, setStoredSendSkip] = useLocalStorage<number | ''>({
    key: 'broadcast-send-skip',
    defaultValue: '',
    getInitialValueInEffect: false,
  });

  const form = useForm<BroadcastFormValues>({
    initialValues: {
      message: storedMessage,
      url: storedUrl,
      sendLimit: storedSendLimit,
      sendSkip: storedSendSkip,
      name: '',
      activeOnly: true,
      withMessagesOnly: true,
    },
    validate: {
      message: (value) => {
        if (value.trim().length === 0) return 'Správa je povinná';
        return null;
      },
      url: (value) => {
        if (value.trim().length === 0) return 'URL je povinné';

        try {
          new URL(value);
          return null;
        } catch {
          return 'Zadajte platné URL';
        }
      },
      sendLimit: (value) => {
        if (typeof value !== 'number' || Number.isNaN(value)) return 'Zadajte limit';
        if (value <= 0) return 'Limit musí byť väčší ako 0';
        return null;
      },
      sendSkip: (value) => {
        if (value === '') return null;
        if (typeof value !== 'number' || Number.isNaN(value)) return 'Offset musí byť číslo';
        if (!Number.isInteger(value)) return 'Offset musí byť celé číslo';
        if (value < 0) return 'Offset nemôže byť záporný';
        return null;
      },
    },
  });

  const [selectedCustomers, setSelectedCustomers] = useState<BroadcastCustomersQuery['customers']>(
    []
  );
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useLocalStorage<number>({
    key: 'broadcast-customers-page-size',
    defaultValue: DEFAULT_BROADCAST_LIMIT,
  });
  const [sortStatus, setSortStatus] = useLocalStorage<DataTableSortStatus<BroadcastCustomer>>({
    key: 'broadcast-customers-sort',
    defaultValue: {
      columnAccessor: 'updatedAt',
      direction: 'desc',
    },
  });

  const { name, activeOnly, withMessagesOnly } = form.values;

  const where = useMemo(
    () => buildWhere({ name, activeOnly, withMessagesOnly }),
    [name, activeOnly, withMessagesOnly]
  );
  const [debouncedWhere] = useDebouncedValue(where, 250);
  const orderBy = useMemo(() => buildOrderBy(sortStatus), [sortStatus]);
  const previewSkip = pageSize * page - pageSize;
  const take = pageSize > 0 ? pageSize : DEFAULT_BROADCAST_LIMIT;

  useEffect(() => {
    setPage(1);
    setSelectedCustomers([]);
  }, [name, activeOnly, withMessagesOnly]);

  useEffect(() => {
    setStoredMessage(form.values.message);
  }, [form.values.message, setStoredMessage]);

  useEffect(() => {
    setStoredUrl(form.values.url);
  }, [form.values.url, setStoredUrl]);

  useEffect(() => {
    if (typeof form.values.sendLimit !== 'number' || form.values.sendLimit <= 0) return;
    setStoredSendLimit(form.values.sendLimit);
  }, [form.values.sendLimit, setStoredSendLimit]);

  useEffect(() => {
    if (form.values.sendSkip === '') {
      setStoredSendSkip('');
      return;
    }

    if (
      typeof form.values.sendSkip === 'number' &&
      Number.isInteger(form.values.sendSkip) &&
      form.values.sendSkip >= 0
    ) {
      setStoredSendSkip(form.values.sendSkip);
    }
  }, [form.values.sendSkip, setStoredSendSkip]);

  const previewVariables = useMemo(
    () => ({
      where: debouncedWhere,
      orderBy,
      skip: previewSkip,
      take,
    }),
    [debouncedWhere, orderBy, previewSkip, take]
  );

  const { data: previewData, loading: previewLoading } = useQuery(broadcastCustomersDocument, {
    variables: previewVariables,
    notifyOnNetworkStatusChange: true,
  });

  const [sendBroadcast, { loading }] = useMutation(sendBroadcastDocument, {
    onCompleted: ({ sendBroadcast: sentCount }) => {
      showNotification({
        title: 'Broadcast odoslaný',
        message: `Správa bola odoslaná ${sentCount} zákazníkom`,
        color: 'green',
      });
    },
  });

  const totalFilteredCount = previewData?.countCustomers ?? 0;
  const tableColumns = useMemo<DataTableColumn<BroadcastCustomer>[]>(
    () => [
      {
        accessor: 'name',
        title: 'Meno',
        sortable: true,
        render: ({ name, avatar }) => (
          <Group gap="sm" wrap="nowrap">
            <Avatar src={avatar} size="sm" />
            <Text size="sm">{name}</Text>
          </Group>
        ),
      },
      {
        accessor: 'active',
        title: 'Aktívny',
        sortable: true,
        render: ({ active }) => (
          <Badge color={active ? 'teal' : 'red'}>{active ? 'Áno' : 'Nie'}</Badge>
        ),
      },
      {
        accessor: 'reason',
        title: 'Reason',
        render: ({ reason }) => (reason && reason.trim().length > 0 ? reason : '-'),
      },
      {
        accessor: 'createdAt',
        title: 'Vytvorený',
        sortable: true,
        render: ({ createdAt }) => dayjs(createdAt).format('DD.MM.YYYY HH:mm'),
      },
      {
        accessor: 'updatedAt',
        title: 'Aktualizovaný',
        sortable: true,
        render: ({ updatedAt }) => dayjs(updatedAt).format('DD.MM.YYYY HH:mm'),
      },
    ],
    []
  );
  const manualSendLimit =
    typeof form.values.sendLimit === 'number' && form.values.sendLimit > 0
      ? form.values.sendLimit
      : DEFAULT_BROADCAST_LIMIT;
  const manualSendSkip =
    typeof form.values.sendSkip === 'number' &&
    Number.isInteger(form.values.sendSkip) &&
    form.values.sendSkip >= 0
      ? form.values.sendSkip
      : null;
  const selectedCount = selectedCustomers.length;
  const sendingSelectedOnly = selectedCount > 0;
  const effectiveSendSkip = manualSendSkip ?? previewSkip;
  const remainingFilteredCustomers = Math.max(totalFilteredCount - effectiveSendSkip, 0);
  const filteredRecipientCount = Math.min(remainingFilteredCustomers, manualSendLimit);
  const filteredRangeStart = filteredRecipientCount > 0 ? effectiveSendSkip + 1 : 0;
  const filteredRangeEnd = effectiveSendSkip + filteredRecipientCount;
  const recipientCount = sendingSelectedOnly ? selectedCount : filteredRecipientCount;
  const filtersSettled = JSON.stringify(where) === JSON.stringify(debouncedWhere);

  const handleSubmit = form.onSubmit((values) => {
    const selectedIds = selectedCustomers.map(({ id }) => id);
    const message = values.message.trim();
    const url = values.url.trim();
    const manualLimit =
      typeof values.sendLimit === 'number' && values.sendLimit > 0
        ? values.sendLimit
        : DEFAULT_BROADCAST_LIMIT;
    const manualSkip =
      typeof values.sendSkip === 'number' &&
      Number.isInteger(values.sendSkip) &&
      values.sendSkip >= 0
        ? values.sendSkip
        : null;
    const effectiveFilteredSkip = manualSkip ?? previewSkip;
    const remainingCustomersAfterSkip = Math.max(totalFilteredCount - effectiveFilteredSkip, 0);
    const effectiveFilteredLimit = Math.min(remainingCustomersAfterSkip, manualLimit);

    const variables =
      selectedIds.length > 0
        ? {
            message,
            url,
            where: {
              id: {
                in: selectedIds,
              },
            },
            limit: selectedIds.length,
            skip: 0,
          }
        : {
            message,
            url,
            where: debouncedWhere,
            orderBy,
            limit: effectiveFilteredLimit,
            skip: effectiveFilteredSkip,
          };

    modals.openConfirmModal({
      title: 'Odoslať broadcast?',
      labels: { confirm: 'Odoslať', cancel: 'Zrušiť' },
      confirmProps: { color: 'indigo' },
      children: (
        <Stack gap="xs">
          <Text>
            {selectedIds.length > 0
              ? `Správa bude odoslaná ${selectedIds.length} označeným zákazníkom.`
              : `Správa bude odoslaná ${effectiveFilteredLimit} zákazníkom podľa aktuálneho filtra (offset ${effectiveFilteredSkip}, limit ${manualLimit}).`}
          </Text>
          <Text c="dimmed" size="sm">
            {selectedIds.length > 0
              ? `Označených zákazníkov: ${selectedIds.length}`
              : effectiveFilteredLimit > 0
                ? `Rozsah odoslania: ${effectiveFilteredSkip + 1} - ${effectiveFilteredSkip + effectiveFilteredLimit} z ${totalFilteredCount}`
                : `Po offsete ${effectiveFilteredSkip} už nie sú žiadni zákazníci na odoslanie`}
          </Text>
          <Paper withBorder p="xs">
            <Text fw={500} mb="xs">
              Snapshot príjemcov
            </Text>
            {selectedIds.length > 0 ? (
              <SnapshotRecipientsTable
                mode="local"
                records={selectedCustomers}
                totalRecords={selectedIds.length}
              />
            ) : (
              <SnapshotRecipientsTable
                mode="query"
                where={debouncedWhere}
                orderBy={orderBy}
                baseSkip={effectiveFilteredSkip}
                totalRecords={effectiveFilteredLimit}
              />
            )}
          </Paper>
          <Text size="xs" c="dimmed">
            Snapshot tabuľka obsahuje{' '}
            {selectedIds.length > 0 ? selectedIds.length : effectiveFilteredLimit} príjemcov
            (stránkované).
          </Text>
          {selectedIds.length === 0 && (
            <Text size="xs" c="dimmed">
              Pri odoslaní bez označenia sa použije filter + zoradenie + offset{' '}
              {effectiveFilteredSkip} + limit {effectiveFilteredLimit}.
            </Text>
          )}
          <Alert variant="light" color="indigo">
            <Stack gap={4}>
              <Text fw={500}>Náhľad správy</Text>
              <Text style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{message}</Text>
              <Text
                size="sm"
                c="dimmed"
                style={{ overflowWrap: 'anywhere', wordBreak: 'break-all' }}
              >
                URL: {url}
              </Text>
            </Stack>
          </Alert>
        </Stack>
      ),
      onConfirm: () => {
        void (async () => {
          // Snapshot the queue's completed/failed counts BEFORE enqueuing so the
          // live panel can show only this send's deltas (no DB persistence).
          let baselineCompleted = 0;
          let baselineFailed = 0;
          try {
            const baselineRes = await client.query({
              query: broadcastQueueStatusDocument,
              fetchPolicy: 'network-only',
            });
            baselineCompleted = baselineRes.data.broadcastQueueStatus.completed;
            baselineFailed = baselineRes.data.broadcastQueueStatus.failed;
          } catch {
            // If the baseline can't be read, fall back to 0 — counts may be
            // slightly inflated by leftover jobs, but progress still tracks.
          }

          const sentAt = Date.now();
          const result = await sendBroadcast({ variables });
          const total = result.data?.sendBroadcast ?? 0;
          setRun({ total, sentAt, baselineCompleted, baselineFailed });
        })();
      },
    });
  });

  return (
    <Stack>
      <Paper withBorder p="md">
        <form onSubmit={handleSubmit}>
          <Stack>
            <Textarea
              label="Správa"
              placeholder="Napíšte broadcast správu..."
              autosize
              minRows={5}
              {...form.getInputProps('message')}
            />
            <TextInput label="URL" placeholder="https://..." {...form.getInputProps('url')} />
            <NumberInput
              label="Limit odoslania (bez označenia)"
              placeholder="Napr. 5000"
              min={1}
              allowDecimal={false}
              allowNegative={false}
              hideControls
              {...form.getInputProps('sendLimit')}
            />
            <NumberInput
              label="Offset odoslania (voliteľné)"
              placeholder={`Prázdne = aktuálna stránka (${previewSkip})`}
              min={0}
              allowDecimal={false}
              allowNegative={false}
              hideControls
              {...form.getInputProps('sendSkip')}
            />
            <Text size="xs" c="dimmed">
              Ak necháte offset prázdny, použije sa aktuálna stránka náhľadu (stránka {page}, offset{' '}
              {previewSkip}).
            </Text>
            <Text size="xs" c="dimmed">
              Ak označíte zákazníkov v tabuľke, odošle sa len označeným a tento limit sa nepoužije.
            </Text>
            <Group justify="space-between">
              <Text size="sm" c="dimmed">
                {sendingSelectedOnly
                  ? `Označení zákazníci: ${selectedCount}`
                  : filteredRecipientCount > 0
                    ? `Cieľ podľa filtra: ${filteredRecipientCount} zákazníkov (rozsah ${filteredRangeStart} - ${filteredRangeEnd} z ${totalFilteredCount}, offset ${effectiveSendSkip}, limit ${manualSendLimit})`
                    : `Po offsete ${effectiveSendSkip} nie sú žiadni zákazníci na odoslanie`}
              </Text>
              <Group>
                {selectedCount > 0 && (
                  <Button
                    variant="subtle"
                    color="gray"
                    onClick={() => setSelectedCustomers([])}
                    type="button"
                  >
                    Zrušiť označenie ({selectedCount})
                  </Button>
                )}
                <Button
                  type="submit"
                  loading={loading}
                  disabled={recipientCount === 0 || previewLoading || !filtersSettled}
                >
                  Odoslať broadcast
                </Button>
              </Group>
            </Group>
          </Stack>
        </form>
      </Paper>
      {run && (
        <BroadcastProgress key={run.sentAt} run={run} onClose={() => setRun(null)} />
      )}
      <Paper withBorder p="md">
        <Group justify="space-between" mb="xs">
          <Text fw={500}>Zákazníci pre broadcast</Text>
          {previewLoading && <Loader size="xs" />}
        </Group>
        <Group grow align="end" mb="sm">
          <TextInput
            label="Meno zákazníka"
            placeholder="Filtrovať podľa mena..."
            {...form.getInputProps('name')}
          />
        </Group>
        <Group mb="md">
          <Checkbox
            label="Len aktívni zákazníci"
            checked={form.values.activeOnly}
            onChange={(event) => form.setFieldValue('activeOnly', event.currentTarget.checked)}
          />
          <Checkbox
            label="Len zákazníci so správami"
            checked={form.values.withMessagesOnly}
            onChange={(event) =>
              form.setFieldValue('withMessagesOnly', event.currentTarget.checked)
            }
          />
        </Group>
        <Text size="sm" c="dimmed" mb="md">
          Zobrazených {previewData?.customers.length ?? 0} z {previewData?.countCustomers ?? 0}{' '}
          zákazníkov podľa aktuálneho filtra.
        </Text>
        <Text size="sm" c="dimmed" mb="md">
          Náhľad je stránkovaný. Bez označenia sa odosiela od zvoleného offsetu (manuálne alebo
          podľa aktuálnej stránky) s nastaveným limitom.
        </Text>
        <DataTable
          withTableBorder
          striped
          highlightOnHover
          minHeight={220}
          fetching={previewLoading}
          records={previewData?.customers ?? []}
          idAccessor="id"
          selectedRecords={selectedCustomers}
          onSelectedRecordsChange={setSelectedCustomers}
          totalRecords={previewData?.countCustomers ?? 0}
          recordsPerPage={pageSize}
          page={page}
          onPageChange={setPage}
          recordsPerPageOptions={[10, 25, 50, 100]}
          onRecordsPerPageChange={(value) => {
            setPageSize(value);
            setPage(1);
          }}
          sortStatus={sortStatus}
          onSortStatusChange={(status) => {
            setSortStatus(status);
            setPage(1);
          }}
          columns={tableColumns}
        />
      </Paper>
    </Stack>
  );
};
